import React, { useEffect, useMemo, useState } from 'react';
import './CortadHistory.css';

const chartColor = '#c2410c';
const YEAR_MIN_DEFAULT = 1982;
const YEAR_MAX_DEFAULT = 2022;

const defaultRange = (ymin, ymax) => {
  const end = ymax;
  const start = Math.max(ymin, end - 3);
  return [start, end];
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatMonthYear = (value) => {
  if (!value || String(value).length < 4) return value || '';
  const text = String(value);
  const year = text.slice(0, 4);
  const month = Number(text.slice(5, 7));
  if (month >= 1 && month <= 12) return `${MONTHS[month - 1]} ${year}`;
  return year;
};

const yearTicks = (points) => {
  const ticks = [];
  const seen = new Set();
  (points || []).forEach((point, index) => {
    const year = String(point.t || '').slice(0, 4);
    if (!year || seen.has(year)) return;
    seen.add(year);
    ticks.push({ index, year });
  });
  if (ticks.length <= 6) return ticks;
  const step = Math.ceil(ticks.length / 5);
  const picked = ticks.filter((_, index) => index % step === 0);
  const last = ticks[ticks.length - 1];
  if (picked[picked.length - 1]?.year !== last.year) picked.push(last);
  return picked;
};

const Sparkline = ({ points, unit = '', height = 118 }) => {
  const values = (points || []).map((item) => item.v).filter((value) => Number.isFinite(value));
  if (values.length < 2) {
    return <div style={{ color: '#6c757d', fontSize: '0.8rem' }}>No series for this cell yet.</div>;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 0.2);
  const width = 420;
  const left = 44;
  const right = width - 10;
  const top = 8;
  const bottom = height - 24;
  const plotW = right - left;
  const plotH = bottom - top;
  const lastIndex = Math.max(points.length - 1, 1);
  const xOf = (index) => left + (index / lastIndex) * plotW;
  const yOf = (value) => bottom - ((value - min) / span) * plotH;
  const coords = points
    .map((item, index) => (
      Number.isFinite(item.v) ? `${xOf(index).toFixed(1)},${yOf(item.v).toFixed(1)}` : null
    ))
    .filter(Boolean)
    .join(' ');
  const yLabels = [max, (min + max) / 2, min];
  const xLabels = yearTicks(points);
  const firstDate = formatMonthYear(points[0]?.t);
  const lastDate = formatMonthYear(points[points.length - 1]?.t);
  const formatY = (value) => (
    Math.abs(value) >= 10 ? value.toFixed(0) : value.toFixed(1)
  );

  return (
    <div className="cortad-sparkline">
      <svg
        className="cortad-sparkline-svg"
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {yLabels.map((value) => (
          <g key={`y-${value}`}>
            <line
              x1={left}
              x2={right}
              y1={yOf(value)}
              y2={yOf(value)}
              stroke="#edf0f2"
              strokeWidth="1"
            />
            <text x={left - 6} y={yOf(value) + 3} textAnchor="end" className="cortad-sparkline-axis">
              {formatY(value)}
            </text>
          </g>
        ))}
        <polyline fill="none" stroke={chartColor} strokeWidth="2" points={coords} />
        {xLabels.map((tick) => (
          <text
            key={tick.year}
            x={xOf(tick.index)}
            y={height - 6}
            textAnchor="middle"
            className="cortad-sparkline-axis"
          >
            {tick.year}
          </text>
        ))}
      </svg>
      <div className="cortad-sparkline-caption">
        {firstDate} – {lastDate} · monthly
      </div>
    </div>
  );
};

const Stat = ({ label, value, unit }) => (
  <div className="cortad-history-stat">
    <div className="cortad-history-stat-label">{label}</div>
    <div className="cortad-history-stat-value">
      {value == null ? '—' : `${value}${unit ? ` ${unit}` : ''}`}
    </div>
  </div>
);

const Loader = ({ start, end }) => (
  <div className="cortad-history-loader">
    <span className="cortad-spinner" aria-hidden="true" />
    <span>Loading {start}–{end}…</span>
  </div>
);

const CortadHistory = ({ latitude, longitude, title }) => {
  const [yearMin, setYearMin] = useState(YEAR_MIN_DEFAULT);
  const [yearMax, setYearMax] = useState(YEAR_MAX_DEFAULT);
  const [startYear, setStartYear] = useState(YEAR_MAX_DEFAULT - 3);
  const [endYear, setEndYear] = useState(YEAR_MAX_DEFAULT);
  const [queryStart, setQueryStart] = useState(YEAR_MAX_DEFAULT - 3);
  const [queryEnd, setQueryEnd] = useState(YEAR_MAX_DEFAULT);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQueryStart(startYear);
      setQueryEnd(endYear);
    }, 280);
    return () => window.clearTimeout(timer);
  }, [startYear, endYear]);

  useEffect(() => {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 90000);
    const url = `/api/public/cortad/series?lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}&start_year=${queryStart}&end_year=${queryEnd}`;
    fetch(url, { signal: controller.signal, cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('series unavailable'))))
      .then((data) => {
        if (cancelled) return;
        setPayload(data);
        if (data?.year_min) setYearMin(data.year_min);
        if (data?.year_max) setYearMax(data.year_max);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.name === 'AbortError') setError('Thermal history timed out. Click again.');
        else setError('Could not load thermal history.');
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [latitude, longitude, queryStart, queryEnd]);

  const years = useMemo(() => {
    const out = [];
    for (let year = yearMin; year <= yearMax; year += 1) out.push(year);
    return out;
  }, [yearMin, yearMax]);

  const pending = loading || startYear !== queryStart || endYear !== queryEnd;
  const last4 = defaultRange(yearMin, yearMax);
  const isLast4 = startYear === last4[0] && endYear === last4[1];
  const isFull = startYear === yearMin && endYear === yearMax;

  const setRange = (nextStart, nextEnd) => {
    let from = Math.max(yearMin, Math.min(yearMax, Number(nextStart)));
    let to = Math.max(yearMin, Math.min(yearMax, Number(nextEnd)));
    if (from > to) [from, to] = [to, from];
    setStartYear(from);
    setEndYear(to);
  };

  const summary = payload?.summary || {};
  const dhw = payload?.charts?.tsa_dhw;
  const sst = payload?.charts?.sst;
  const dhwPoints = (dhw?.dates || []).map((date, index) => ({ t: date, v: dhw?.values?.[index] }));
  const sstPoints = (sst?.dates || []).map((date, index) => ({ t: date, v: sst?.values?.[index] }));

  return (
    <div className="cortad-history">
      <div className="cortad-history-title">{title || 'Thermal history'}</div>
      <div className="cortad-history-sub">
        CoRTAD 4 km cell · {startYear}–{endYear}
      </div>
      <div className="cortad-history-range">
        <div className="cortad-history-range-row">
          <span>Year range</span>
          <strong>{startYear} – {endYear}</strong>
        </div>
        <div className="cortad-history-sliders">
          <input
            type="range"
            min={yearMin}
            max={yearMax}
            step={1}
            value={startYear}
            aria-label="Start year"
            onChange={(event) => setRange(event.target.value, endYear)}
          />
          <input
            type="range"
            min={yearMin}
            max={yearMax}
            step={1}
            value={endYear}
            aria-label="End year"
            onChange={(event) => setRange(startYear, event.target.value)}
          />
        </div>
        <div className="cortad-history-presets">
          <button type="button" className={isLast4 ? 'is-active' : ''} onClick={() => setRange(...last4)}>
            Last 4 years
          </button>
          <button type="button" className={isFull ? 'is-active' : ''} onClick={() => setRange(yearMin, yearMax)}>
            Full record
          </button>
          <select
            aria-label="Start year"
            value={startYear}
            onChange={(event) => setRange(event.target.value, endYear)}
          >
            {years.map((year) => (
              <option key={`from-${year}`} value={year}>{year}</option>
            ))}
          </select>
          <select
            aria-label="End year"
            value={endYear}
            onChange={(event) => setRange(startYear, event.target.value)}
          >
            {years.map((year) => (
              <option key={`to-${year}`} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>
      {error && <div style={{ color: '#6c757d', fontSize: '0.8rem' }}>{error}</div>}
      <div className={`cortad-history-chart-wrap${pending ? ' is-loading' : ''}`}>
        {pending && <Loader start={startYear} end={endYear} />}
        {!pending && payload && !payload.found && (
          <div style={{ color: '#6c757d', fontSize: '0.8rem' }}>{payload.message}</div>
        )}
        {payload?.found && (
          <div className="cortad-history-charts">
            <div className="cortad-history-chart-label">TSA DHW (°C-weeks)</div>
            <Sparkline points={dhwPoints} unit="°C-weeks" />
            <div className="cortad-history-chart-label" style={{ marginTop: 8 }}>SST (°C)</div>
            <Sparkline points={sstPoints} unit="°C" />
            <div className="cortad-history-stats">
              <Stat label="Mean SST" value={summary.mean_sst} unit="°C" />
              <Stat label="Maximum SST" value={summary.max_sst} unit="°C" />
              <Stat label="Maximum SSTA" value={summary.max_ssta} unit="°C" />
              <Stat label="Maximum TSA" value={summary.max_tsa} unit="°C" />
              <Stat label="Maximum TSA DHW" value={summary.max_tsa_dhw} unit="°C-weeks" />
              <Stat label="Stress events" value={summary.stress_events} />
            </div>
            {!!summary.severe_years?.length && (
              <div style={{ fontSize: '0.8rem' }}>
                <strong>Severe years</strong> {summary.severe_years.join(', ')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CortadHistory;
